import { useCallback, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { cn } from '@/lib/utils';
import type { Contact } from '@/types/email';

import {
  extractDisplayName,
  extractEmailAddress as extractEmail,
  isValidAddressChip as isValidEmail,
  splitAddressList,
} from '@/lib/addressParsing';

/** Parse a chip into display parts */
function parseChipDisplay(chip: string): { name?: string; email: string } {
  const name = extractDisplayName(chip);
  return name ? { name, email: extractEmail(chip) } : { email: chip };
}

/** Payload attached to a chip drag; consumed by RecipientFields' onDragEnd. */
export interface ChipDragData {
  field: string;
  index: number;
  chip: string;
}

function ChipLabel({ chip }: { chip: string }) {
  const display = parseChipDisplay(chip);
  return (
    <span className="max-w-[250px] truncate">
      {display.name ? (
        <>
          {display.name}{' '}
          <span className="text-muted-foreground">&lt;{display.email}&gt;</span>
        </>
      ) : (
        display.email
      )}
    </span>
  );
}

/** Floating chip rendered in RecipientFields' DragOverlay while dragging. */
export function ChipDragPreview({ chip }: { chip: string }) {
  return (
    <span className="inline-flex cursor-grabbing items-center rounded-full bg-primary/15 px-1.5 py-0.5 text-xs font-medium text-foreground shadow-md ring-1 ring-primary/40">
      <ChipLabel chip={chip} />
    </span>
  );
}

interface DraggableChipProps {
  dndId: string;
  chip: string;
  index: number;
  onRemove: (index: number) => void;
}

function DraggableChip({ dndId, chip, index, onRemove }: DraggableChipProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `${dndId}-chip-${index}`,
    data: { field: dndId, index, chip } satisfies ChipDragData,
  });
  return (
    <span
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      tabIndex={-1}
      className={cn(
        'inline-flex cursor-grab touch-none select-none items-center gap-0.5 rounded-full bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-foreground',
        isDragging && 'opacity-30',
      )}
    >
      <ChipLabel chip={chip} />
      <button
        type="button"
        tabIndex={-1}
        onClick={(e) => {
          e.stopPropagation();
          onRemove(index);
        }}
        className="ml-0.5 rounded-full text-muted-foreground hover:text-foreground"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

interface ChipInputProps {
  id?: string;
  /**
   * Droppable/draggable identity within RecipientFields' DndContext
   * ('to' | 'cc' | 'bcc'). ChipInput must be rendered inside that context.
   */
  dndId: string;
  chips: string[];
  onChipsChange: (chips: string[]) => void;
  inputValue: string;
  onInputChange: (value: string) => void;
  suggestions: Contact[];
  onSuggestionSelect: (contact: Contact) => void;
  placeholder?: string;
  autoFocus?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  rightContent?: React.ReactNode;
}

export function ChipInput({
  id,
  dndId,
  chips,
  onChipsChange,
  inputValue,
  onInputChange,
  suggestions,
  onSuggestionSelect,
  placeholder,
  autoFocus,
  onFocus,
  onBlur,
  rightContent,
}: ChipInputProps) {
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);
  const [invalidInput, setInvalidInput] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: dndId });

  const commitValue = useCallback(
    (raw: string) => {
      const trimmed = raw.trim();
      if (!trimmed) return false;
      // Avoid duplicates — compare by email part
      const trimmedEmail = extractEmail(trimmed).toLowerCase();
      if (chips.some((c) => extractEmail(c).toLowerCase() === trimmedEmail)) {
        onInputChange('');
        setInvalidInput(false);
        return true;
      }
      // Validate email format
      if (!isValidEmail(trimmed)) {
        setInvalidInput(true);
        return false;
      }
      setInvalidInput(false);
      onChipsChange([...chips, trimmed]);
      onInputChange('');
      setHighlightedIndex(-1);
      return true;
    },
    [chips, onChipsChange, onInputChange],
  );

  const handleInputChange = useCallback(
    (value: string) => {
      // Check for comma/semicolon separators to auto-commit. Quote-aware
      // splitting: a comma INSIDE a quoted display name or <> must not split
      // the chip (G10).
      if (value.includes(',') || value.includes(';')) {
        const endsWithSeparator = /[,;]\s*$/.test(value);
        const allParts = splitAddressList(value);
        if (allParts.length <= 1 && !endsWithSeparator) {
          // The separator is inside quotes/brackets — keep typing.
          onInputChange(value);
          setHighlightedIndex(-1);
          setInvalidInput(false);
          return;
        }
        const toCommit = endsWithSeparator ? allParts : allParts.slice(0, -1);
        const remainder = endsWithSeparator ? '' : allParts[allParts.length - 1] || '';
        let newChips = [...chips];
        for (const part of toCommit) {
          const trimmed = part.trim();
          if (trimmed && isValidEmail(trimmed) && !newChips.some((c) => extractEmail(c).toLowerCase() === extractEmail(trimmed).toLowerCase())) {
            newChips = [...newChips, trimmed];
          }
        }
        if (newChips.length !== chips.length) {
          onChipsChange(newChips);
        }
        onInputChange(remainder);
        setHighlightedIndex(-1);
      } else {
        onInputChange(value);
        setHighlightedIndex(-1);
        setInvalidInput(false);
      }
    },
    [chips, onChipsChange, onInputChange],
  );

  const removeChip = useCallback(
    (index: number) => {
      onChipsChange(chips.filter((_, i) => i !== index));
    },
    [chips, onChipsChange],
  );

  const visibleSuggestions = isFocused ? suggestions.slice(0, 6) : [];

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        if (visibleSuggestions.length > 0) {
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < visibleSuggestions.length - 1 ? prev + 1 : 0,
          );
        }
        return;
      }

      if (e.key === 'ArrowUp') {
        if (visibleSuggestions.length > 0) {
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : visibleSuggestions.length - 1,
          );
        }
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < visibleSuggestions.length) {
          onSuggestionSelect(visibleSuggestions[highlightedIndex]);
          setHighlightedIndex(-1);
        } else if (visibleSuggestions.length > 0) {
          // User typed a name but never arrowed down — pick the visible top
          // suggestion rather than committing the raw text, which would fail
          // email validation for a display name like "John Smith" and flash
          // invalid. Raw text is only committed when there are no suggestions.
          onSuggestionSelect(visibleSuggestions[0]);
          setHighlightedIndex(-1);
        } else {
          commitValue(inputValue);
        }
        return;
      }

      if (e.key === 'Escape') {
        setHighlightedIndex(-1);
        return;
      }

      if (e.key === 'Tab') {
        if (inputValue.trim()) {
          commitValue(inputValue);
          // Allow default tab behavior to move to next field
        }
        setHighlightedIndex(-1);
        return;
      }

      if (e.key === 'Backspace' && !inputValue) {
        if (chips.length > 0) {
          removeChip(chips.length - 1);
        }
        return;
      }
    },
    [chips, commitValue, highlightedIndex, inputValue, onSuggestionSelect, removeChip, visibleSuggestions],
  );

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    onFocus?.();
  }, [onFocus]);

  const handleBlur = useCallback(() => {
    // Small delay to allow suggestion clicks to register
    setTimeout(() => {
      setIsFocused(false);
      setHighlightedIndex(-1);
      // Commit any remaining text on blur
      if (inputValue.trim()) {
        commitValue(inputValue);
      }
      onBlur?.();
    }, 150);
  }, [commitValue, inputValue, onBlur]);

  const handleSuggestionClick = useCallback(
    (contact: Contact) => {
      onSuggestionSelect(contact);
      setHighlightedIndex(-1);
      inputRef.current?.focus();
    },
    [onSuggestionSelect],
  );

  return (
    <div className="relative">
      <div
        ref={setDropRef}
        className={cn(
          'flex min-h-9 flex-wrap items-center gap-1 rounded-md border border-input bg-transparent px-2 py-1 text-sm shadow-xs transition-colors',
          invalidInput && 'border-destructive ring-1 ring-destructive/20',
          isFocused && !invalidInput && 'border-primary/40 ring-1 ring-primary/20',
          isOver && 'border-primary/60 bg-primary/5 ring-1 ring-primary/30',
        )}
        onClick={() => inputRef.current?.focus()}
      >
        {chips.map((chip, index) => (
          <DraggableChip
            key={`${chip}-${index}`}
            dndId={dndId}
            chip={chip}
            index={index}
            onRemove={removeChip}
          />
        ))}
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={chips.length === 0 ? placeholder : undefined}
          autoFocus={autoFocus}
          autoComplete="off"
          className="min-w-[80px] flex-1 bg-transparent py-0.5 text-sm outline-none placeholder:text-muted-foreground ml-1"
          style={{ width: `${Math.max(80, (inputValue.length + 1) * 8)}px` }}
        />
        {rightContent && (
          <div className="ml-auto flex shrink-0 items-center gap-0.5">
            {rightContent}
          </div>
        )}
      </div>

      {visibleSuggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-10 mt-1 rounded-md border bg-popover p-1 shadow-md">
          {visibleSuggestions.map((contact, index) => (
            <button
              key={contact.id}
              type="button"
              className={cn(
                'flex w-full items-start rounded-sm px-2 py-1.5 text-left text-xs transition-colors',
                index === highlightedIndex
                  ? 'bg-accent text-accent-foreground'
                  : 'hover:bg-accent/50',
              )}
              onPointerDown={(e) => e.preventDefault()}
              onClick={() => handleSuggestionClick(contact)}
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{contact.displayName || contact.email}</div>
                {contact.displayName && (
                  <div className="text-muted-foreground truncate">{contact.email}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
